import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Camera, Crown, MagnifyingGlass, Microphone, PushPin, SpeakerHigh, SpeakerSlash, Trash, UserMinus, UserPlus, UsersThree, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { messagesApi, usersApi } from "@/lib/apiClient";
import ProtectedAttachment from "@/components/messages/ProtectedAttachment";
import { ProtectedConversationAvatar } from "@/components/messages/ProtectedConversationAvatar";

const ROLE_OPTIONS = ["admin", "moderator", "member"];

export function GroupDetailsDrawer({ open, conversation, currentUser, savedItems, onClose, onConversationChange, onConversationLeft, onMessageJump }) {
    const fileRef = useRef(null);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [busy, setBusy] = useState(false);
    const [memberQuery, setMemberQuery] = useState("");
    const [memberResults, setMemberResults] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [mediaItems, setMediaItems] = useState([]);
    const [mediaLoading, setMediaLoading] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null); // "leave" | "delete" | null

    useEffect(() => {
        if (!open || !conversation) return;
        setTitle(conversation.title || "");
        setDescription(conversation.description || "");
        setMemberQuery("");
        setMemberResults([]);
        setSearchQuery("");
        setSearchResults([]);
        setMediaLoading(true);
        messagesApi.conversationMedia(conversation.id)
            .then((data) => setMediaItems(data.items || []))
            .catch(() => setMediaItems([]))
            .finally(() => setMediaLoading(false));
    }, [open, conversation]);

    useEffect(() => {
        if (!open || !conversation || memberQuery.trim().length < 1) {
            setMemberResults([]);
            return undefined;
        }
        const timer = setTimeout(() => {
            usersApi.search({ q: memberQuery.trim(), limit: 8 })
                .then((data) => {
                    const list = data?.items || [];
                    const existingIds = new Set((conversation.members || []).map((member) => member.user_id));
                    setMemberResults(list.filter((item) => !existingIds.has(item.id)));
                })
                .catch(() => setMemberResults([]));
        }, 180);
        return () => clearTimeout(timer);
    }, [open, conversation, memberQuery]);

    useEffect(() => {
        if (!open || !conversation || searchQuery.trim().length < 1) {
            setSearchResults([]);
            return undefined;
        }
        const timer = setTimeout(() => {
            messagesApi.searchConversation(conversation.id, searchQuery.trim())
                .then((data) => setSearchResults(data.items || []))
                .catch(() => setSearchResults([]));
        }, 200);
        return () => clearTimeout(timer);
    }, [open, conversation, searchQuery]);

    const canEditGroup = ["creator", "admin"].includes(conversation?.role);
    const canManageMembers = ["creator", "admin", "moderator"].includes(conversation?.role);
    const canSetRoles = ["creator", "admin"].includes(conversation?.role);
    const canTransferOwnership = conversation?.role === "creator";
    const isMuted = Boolean(conversation?.muted_until && new Date(conversation.muted_until).getTime() > Date.now());
    const savedForConversation = useMemo(() => (savedItems || []).filter((item) => item.message?.conversation_id === conversation?.id), [savedItems, conversation?.id]);

    if (!conversation) return null;

    const submitGroupDetails = async () => {
        setBusy(true);
        try {
            const updated = await messagesApi.updateConversation(conversation.id, { title: title.trim(), description: description.trim() || null });
            onConversationChange?.(updated);
            toast.success("Group details updated.");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not update this group.");
        } finally {
            setBusy(false);
        }
    };

    const inviteMember = async (handle) => {
        try {
            const result = await messagesApi.inviteMembers(conversation.id, [handle]);
            setMemberResults((prev) => prev.filter((item) => item.handle !== handle));
            toast.success(result.invited?.length ? "Invite sent." : "Passport skipped that invite.");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not invite this member.");
        }
    };

    const updateRole = async (userId, role) => {
        try {
            await messagesApi.setGroupRole(conversation.id, userId, role);
            const updatedMembers = (conversation.members || []).map((m) => (m.user_id === userId ? { ...m, role } : m));
            onConversationChange?.({ ...conversation, members: updatedMembers });
            toast.success("Member role updated.");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not update that role.");
        }
    };

    const removeMember = async (userId) => {
        try {
            await messagesApi.removeGroupMember(conversation.id, userId);
            const updatedMembers = (conversation.members || []).filter((m) => m.user_id !== userId);
            onConversationChange?.({
                ...conversation,
                members: updatedMembers,
                member_count: Math.max(0, (conversation.member_count || 1) - 1),
            });
            toast.success("Member removed.");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not remove that member.");
        }
    };

    const handleTransferOwnership = async (targetMember) => {
        const confirmed = window.confirm(`Transfer group ownership to ${targetMember.name || targetMember.handle || "this traveler"}? You will become an admin.`);
        if (!confirmed) return;
        try {
            const updated = await messagesApi.transferOwnership(conversation.id, targetMember.user_id);
            onConversationChange?.(updated);
            toast.success("Group ownership transferred successfully.");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not transfer ownership.");
        }
    };

    const leaveGroup = async () => {
        try {
            await messagesApi.leaveGroup(conversation.id);
            toast.success("You left the group.");
            setConfirmAction(null);
            onConversationLeft?.();
        } catch (error) {
            setConfirmAction(null);
            toast.error(error?.response?.data?.detail || "Could not leave this group.");
        }
    };

    const deleteGroup = async () => {
        try {
            await messagesApi.deleteConversation(conversation.id);
            toast.success("Group deleted.");
            setConfirmAction(null);
            onConversationLeft?.();
        } catch (error) {
            setConfirmAction(null);
            toast.error(error?.response?.data?.detail || "Could not delete this group.");
        }
    };

    const requestConfirm = (action) => setConfirmAction(action);
    const cancelConfirm = () => setConfirmAction(null);
    const executeConfirmedAction = () => { if (confirmAction === "leave") leaveGroup(); else if (confirmAction === "delete") deleteGroup(); };

    const toggleMute = async () => {
        try {
            const mutedUntil = isMuted ? null : new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString();
            const updated = await messagesApi.muteConversation(conversation.id, mutedUntil);
            onConversationChange?.(updated);
            toast.success(isMuted ? "Group notifications resumed." : "Group muted for 30 days.");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not update notifications.");
        }
    };

    const uploadAvatar = async (file) => {
        if (!file) return;
        try {
            const updated = await messagesApi.uploadGroupAvatar(conversation.id, file);
            onConversationChange?.(updated);
            toast.success("Group avatar updated.");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not update the group avatar.");
        } finally {
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[110] flex justify-end" style={{ background: "rgba(5,10,17,0.7)", backdropFilter: "blur(10px)" }} onClick={onClose} data-testid="group-details-drawer">
                    <motion.aside initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }} transition={{ type: "spring", damping: 26, stiffness: 240 }} onClick={(event) => event.stopPropagation()} className="w-full max-w-[34rem] h-full overflow-y-auto p-5 md:p-6" style={{ background: "rgba(5,10,17,0.98)", borderLeft: "1px solid rgba(212,175,55,0.18)" }}>
                        <div className="flex items-start justify-between gap-4 mb-6">
                            <div>
                                <div className="overline mb-2">Group details</div>
                                <h3 className="font-serif text-3xl text-white" data-testid="group-details-title">{conversation.title}</h3>
                                <div className="text-sm text-white/50 mt-2">Manage members, media, notifications, and search inside this group.</div>
                            </div>
                            <button type="button" onClick={onClose} className="w-10 h-10 rounded-full glass grid place-items-center" data-testid="group-details-close"><X size={16} /></button>
                        </div>

                        <div className="rounded-[28px] p-5 mb-5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                            <div className="flex items-center gap-4">
                                <div className="w-20 h-20 rounded-[28px] overflow-hidden shrink-0" style={{ background: "rgba(255,255,255,0.06)" }}>
                                    <ProtectedConversationAvatar
                                        mediaId={conversation.avatar_media_id}
                                        className="w-full h-full object-cover"
                                        testId="group-details-avatar"
                                        fallback={<div className="w-full h-full grid place-items-center text-white/65"><UsersThree size={28} /></div>}
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-white/80">{conversation.member_count || conversation.members?.length || 0} members</div>
                                    <div className="text-xs text-white/45 mt-1">Your role: {conversation.role}</div>
                                    {canEditGroup && (
                                        <>
                                            <input value={title} onChange={(event) => setTitle(event.target.value)} className="w-full mt-3 rounded-2xl px-4 py-3 bg-black/25 text-white border border-white/10 outline-none transition-all focus:relative focus:z-10 focus:border-white/60 focus:ring-1 focus:ring-white/30" data-testid="group-details-name-input" />
                                            <textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={3} className="w-full mt-3 rounded-2xl px-4 py-3 bg-black/25 text-white border border-white/10 resize-none outline-none transition-all focus:relative focus:z-10 focus:border-white/60 focus:ring-1 focus:ring-white/30" data-testid="group-details-description-input" />
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-3 mt-4">
                                {canEditGroup && <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost text-sm !py-2 !px-4" data-testid="group-details-avatar-button"><Camera size={14} /> Update avatar</button>}
                                <button type="button" onClick={toggleMute} className="btn-ghost text-sm !py-2 !px-4" data-testid="group-details-mute-button">{isMuted ? <SpeakerHigh size={14} /> : <SpeakerSlash size={14} />}{isMuted ? " Unmute" : " Mute"}</button>
                                {canEditGroup && <button type="button" onClick={submitGroupDetails} disabled={busy} className="btn-gold text-sm !py-2 !px-4 disabled:opacity-50" data-testid="group-details-save-button">Save changes</button>}
                            </div>
                            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(event) => uploadAvatar(event.target.files?.[0])} data-testid="group-details-avatar-input" />
                        </div>

                        <div className="rounded-[28px] p-5 mb-5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="group-members-panel">
                            <div className="text-sm text-white mb-3">Members</div>
                            {canManageMembers && (
                                <>
                                    <div className="relative mb-3 focus-within:z-10">
                                        <MagnifyingGlass size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                                        <input value={memberQuery} onChange={(event) => setMemberQuery(event.target.value)} placeholder="Search travelers to invite" className="w-full rounded-2xl pl-10 pr-4 py-3 bg-black/25 text-white border border-white/10 outline-none transition-all focus:relative focus:z-10 focus:border-white/60 focus:ring-1 focus:ring-white/30" data-testid="group-details-invite-search" />
                                    </div>
                                    {!!memberResults.length && (
                                        <div className="space-y-2 mb-4" data-testid="group-details-invite-results">
                                            {memberResults.slice(0, 5).map((item) => (
                                                <div key={item.id} className="rounded-2xl px-4 py-3 flex items-center justify-between gap-3" style={{ background: "rgba(255,255,255,0.03)" }} data-testid={`group-details-invite-result-${item.handle}`}>
                                                    <div>
                                                        <div className="text-sm text-white">{item.name}</div>
                                                        <div className="text-xs text-white/45">@{item.handle}</div>
                                                    </div>
                                                    <button type="button" onClick={() => inviteMember(item.handle)} className="btn-ghost text-xs !py-2 !px-3" data-testid={`group-details-invite-button-${item.handle}`}><UserPlus size={12} /> Invite</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}

                            <div className="space-y-3" data-testid="group-details-members-list">
                                {(conversation.members || []).map((member) => {
                                    const isSelf = member.user_id === currentUser?.id;
                                    return (
                                        <div key={member.user_id} className="rounded-2xl px-4 py-3 flex items-center gap-3" style={{ background: "rgba(255,255,255,0.03)" }} data-testid={`group-member-${member.user_id}`}>
                                            <img src={member.avatar_url || `https://api.dicebear.com/7.x/notionists/svg?seed=${member.handle || member.user_id}`} alt="" className="w-11 h-11 rounded-full object-cover" />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm text-white truncate flex items-center gap-1.5">
                                                    <span>{member.name || member.handle || "Traveler"}</span>
                                                    {member.role === "creator" && <Crown size={14} className="text-[#D4AF37] shrink-0" weight="fill" title="Group Creator" />}
                                                </div>
                                                <div className="text-xs text-white/45 truncate">@{member.handle || member.user_id} · <span className={member.role === "creator" ? "text-[#D4AF37] font-medium" : ""}>{member.role}</span></div>
                                            </div>
                                            {canSetRoles && !isSelf && member.role !== "creator" && (
                                                <select value={member.role} onChange={(event) => updateRole(member.user_id, event.target.value)} className="rounded-xl px-3 py-2 bg-black/35 text-white text-xs border" style={{ borderColor: "rgba(255,255,255,0.08)" }} data-testid={`group-member-role-${member.user_id}`}>
                                                    {ROLE_OPTIONS.map((role) => <option key={role} value={role}>{role}</option>)}
                                                </select>
                                            )}
                                            {canTransferOwnership && !isSelf && member.role !== "creator" && (
                                                <button type="button" onClick={() => handleTransferOwnership(member)} className="w-9 h-9 rounded-full grid place-items-center bg-white/5 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-colors" title="Transfer group ownership" data-testid={`group-member-transfer-${member.user_id}`}><Crown size={14} /></button>
                                            )}
                                            {canManageMembers && !isSelf && member.role !== "creator" && (
                                                <button type="button" onClick={() => removeMember(member.user_id)} className="w-9 h-9 rounded-full grid place-items-center bg-white/5 text-[#F4A28F] hover:bg-[#F4A28F]/20 transition-colors" title="Remove member" data-testid={`group-member-remove-${member.user_id}`}><UserMinus size={14} /></button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="rounded-[28px] p-5 mb-5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="group-search-panel">
                            <div className="text-sm text-white mb-3">Search this thread</div>
                            <div className="relative mb-3">
                                <MagnifyingGlass size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                                <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Find messages, people, or context" className="w-full rounded-2xl pl-10 pr-4 py-3 bg-black/25 text-white border" style={{ borderColor: "rgba(255,255,255,0.08)" }} data-testid="group-thread-search-input" />
                            </div>
                            <div className="space-y-2 max-h-60 overflow-y-auto" data-testid="group-thread-search-results">
                                {searchQuery.trim().length < 1 ? <div className="text-sm text-white/40">Search within this thread to pull up messages fast.</div> : searchResults.length === 0 ? <div className="text-sm text-white/40">No thread matches yet.</div> : searchResults.map((message) => (
                                    <div
                                        key={message.id}
                                        className="rounded-2xl px-4 py-3 cursor-pointer transition-colors hover:bg-white/5 active:bg-white/8"
                                        style={{ background: "rgba(255,255,255,0.03)" }}
                                        data-testid={`group-thread-search-result-${message.id}`}
                                        onClick={() => {
                                            onClose?.();
                                            setTimeout(() => onMessageJump?.(message.id), 120);
                                        }}
                                        title="Jump to this message"
                                    >
                                        <div className="text-xs text-white/45">{message.sender_name || message.sender_handle}</div>
                                        <div className="text-sm text-white mt-1 line-clamp-3">{message.text || message.type || "Attachment"}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="rounded-[28px] p-5 mb-5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="group-media-panel">
                            <div className="flex items-center justify-between gap-3 mb-3">
                                <div className="text-sm text-white">Media & attachments</div>
                                <div className="text-xs text-white/45">{mediaItems.length} items</div>
                            </div>
                            {mediaLoading ? <div className="text-sm text-white/40">Loading media…</div> : mediaItems.length === 0 ? <div className="text-sm text-white/40">No shared media yet.</div> : (
                                <div className="space-y-3" data-testid="group-media-list">
                                    {mediaItems.map((item) => (
                                        <div key={item.id} className="rounded-2xl p-3" style={{ background: "rgba(255,255,255,0.03)" }} data-testid={`group-media-item-${item.id}`}>
                                            <ProtectedAttachment attachment={item} testId={`group-media-attachment-${item.id}`} />
                                            <div className="text-[11px] text-white/45 mt-2">{item.sender_name || item.sender_handle} · {item.kind === "voice" ? <span className="inline-flex items-center gap-1"><Microphone size={11} /> Voice</span> : item.kind}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="rounded-[28px] p-5 mb-5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="group-saved-panel">
                            <div className="text-sm text-white mb-3">Saved in this thread</div>
                            <div className="space-y-2">
                                {savedForConversation.length === 0 ? <div className="text-sm text-white/40">No saved highlights from this group yet.</div> : savedForConversation.map((item) => (
                                    <div key={item.message.id} className="rounded-2xl px-4 py-3" style={{ background: "rgba(255,255,255,0.03)" }} data-testid={`group-saved-item-${item.message.id}`}>
                                        <div className="text-[11px] text-white/45 inline-flex items-center gap-1"><PushPin size={11} /> Saved highlight</div>
                                        <div className="text-sm text-white mt-1 line-clamp-2">{item.message.text || item.message.type || "Attachment"}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex gap-3 flex-wrap items-center" data-testid="group-danger-zone">
                            {confirmAction ? (
                                <div className="flex-1 rounded-2xl px-4 py-3 flex items-center justify-between gap-4" style={{ background: "rgba(244,162,143,0.08)", border: "1px solid rgba(244,162,143,0.2)" }} data-testid="group-confirm-prompt">
                                    <span className="text-sm text-[#F4A28F]">
                                        {confirmAction === "leave" ? "Leave this group? You'll need to be re-invited to rejoin." : "Permanently delete this group and all its messages?"}
                                    </span>
                                    <div className="flex gap-2 shrink-0">
                                        <button type="button" onClick={cancelConfirm} className="text-xs text-white/55 px-3 py-1.5 rounded-xl" style={{ background: "rgba(255,255,255,0.06)" }} data-testid="group-confirm-cancel">Cancel</button>
                                        <button type="button" onClick={executeConfirmedAction} className="text-xs text-[#F4A28F] px-3 py-1.5 rounded-xl font-medium" style={{ background: "rgba(244,162,143,0.15)", border: "1px solid rgba(244,162,143,0.3)" }} data-testid="group-confirm-yes">{confirmAction === "leave" ? "Yes, leave" : "Yes, delete"}</button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <button type="button" onClick={() => requestConfirm("leave")} className="btn-ghost text-sm !py-2.5 !px-4" style={{ color: "#F4A28F", borderColor: "rgba(244,162,143,0.24)" }} data-testid="group-leave-button">Leave group</button>
                                    {conversation.role === "creator" && <button type="button" onClick={() => requestConfirm("delete")} className="btn-ghost text-sm !py-2.5 !px-4" style={{ color: "#F4A28F", borderColor: "rgba(244,162,143,0.24)" }} data-testid="group-delete-button"><Trash size={14} /> Delete group</button>}
                                </>
                            )}
                        </div>
                    </motion.aside>
                </motion.div>
            )}
        </AnimatePresence>
    );
}